import { FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { Header } from "@/components/Header";
import { Flex, Group, Text } from "@/eworkitComponents";
import { FormikForm, FormikTextInput } from "@/formikComponents";
import {
  PasswordValidation,
  passwordValidationSchema,
  usePasswordValidation,
} from "@/views/components/Forms/PasswordValidation";
import { Messenger } from "@/views/components/Messenger";

export const FORM_MESSENGER_CONTEXT = "password-setup-form";

export const validationSchema = Yup.object().shape({
  password: Yup.string().required(VALIDATION_TYPES.REQUIRED).concat(passwordValidationSchema),
  confirmPassword: Yup.string()
    .required(VALIDATION_TYPES.REQUIRED)
    .oneOf([Yup.ref("password"), ""], VALIDATION_TYPES.FIELDS_NOT_MATCH),
});

export interface IPasswordSetupFormValues {
  password: string;
  confirmPassword: string;
}

export const FORM_EMPTY_INITIAL_VALUES = { password: "", confirmPassword: "" };

interface Props {
  form: FormikProps<IPasswordSetupFormValues>;
  token: string;
}

export const PasswordSetupForm: FC<Props> = memo(({ form, token }) => {
  const passwordErrors = usePasswordValidation(form.values.password);
  const { t } = useTranslation("public");

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="xl">
        <Flex innerMargin="md" direction="column">
          <Flex direction="column" innerMargin="lg">
            <Header size="lg" weight="bold" color="black" align="center">
              {t("SESSION_SIGNUP.USER_FORM.RESET_PASSWORD")}
            </Header>
            <Messenger context={FORM_MESSENGER_CONTEXT} />

            <div>
              <Group label={t("SESSION_PASSWORD_SETUP.PLACEHOLDER_PASSWORD")} direction="column">
                <FormikTextInput
                  size="lg"
                  value={form.values.password}
                  type="password"
                  name="password"
                  testName="password"
                  required
                />
                <FormikError
                  name="password"
                  data-test="error-password"
                  overrideMessages={{
                    PASSWORD_BAD_CHAR: t("SESSION_PASSWORD_SETUP.ERROR_MESSAGES.PASSWORD_BAD_CHAR"),
                  }}
                />
              </Group>
            </div>

            <div>
              <Group label={t("SESSION_PASSWORD_SETUP.PLACEHOLDER_CONFIRM_PASSWORD")} direction="column">
                <FormikTextInput
                  size="lg"
                  value={form.values.confirmPassword}
                  type="password"
                  name="confirmPassword"
                  testName="confirmPassword"
                  required
                />
                <FormikError name="confirmPassword" data-test="error-confirmPassword" />
              </Group>
            </div>
          </Flex>
          <Flex direction="column">
            <PasswordValidation errors={passwordErrors} />
          </Flex>
        </Flex>
        <div>
          <Button
            size="lg"
            display="block"
            onClick={form.submitForm}
            disabled={form.isSubmitting || !token}
            testName="setup">
            <Text>{t("SESSION_PASSWORD_RESET.BUTTON_RESET")}</Text>
          </Button>
        </div>
      </Flex>
    </FormikForm>
  );
});
